import {HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  map,
  EMPTY,
  Observable,
  ReplaySubject,
  catchError,
  of,
  shareReplay,
  switchMap,
  throwError, tap
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Permission } from '../utils/permissions.guard';
import {AuthInfo, AuthPermission} from "./auth.model";
import { APIResponse } from "../../shared/data-access/api-response.model";
import { AgreementTemplate as Agreement } from "../../admin/data-access/agreement.model";
import {catchApiError, toApiErrorResponse} from "../../shared/utils/api-error";
import {encodeParam} from "../../shared/utils/query-string";

export const authFactory = (authService: AuthService) => () => {
  const token = localStorage.getItem('token');
  authService.setToken(token);
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private rawHttp: HttpClient;

  constructor(private backend: HttpBackend) {
    // This instance bypasses all interceptors
    this.rawHttp = new HttpClient(backend);
  }
  // injections
  private httpClient = inject(HttpClient);
  private router = inject(Router);

  private lastKnownPrivileges: APIResponse | null = null;

  requiredAgreements: Agreement[] = [];
  optionalAgreements: Agreement[] = [];

  listOfProfileImages: string[] = [
    '../../../assets/images/profile-images/profile-image-0.svg',
    '../../../assets/images/profile-images/profile-image-1.svg',
    '../../../assets/images/profile-images/profile-image-2.svg',
    '../../../assets/images/profile-images/profile-image-3.svg',
    '../../../assets/images/profile-images/profile-image-4.svg',
    '../../../assets/images/profile-images/profile-image-5.svg',
    '../../../assets/images/profile-images/profile-image-6.svg',
    '../../../assets/images/profile-images/profile-image-7.svg',
    '../../../assets/images/profile-images/profile-image-8.svg',
    '../../../assets/images/profile-images/profile-image-9.svg',
    '../../../assets/images/profile-images/profile-image-10.svg',
    '../../../assets/images/profile-images/profile-image-11.svg',
    '../../../assets/images/profile-images/profile-image-12.svg',
    '../../../assets/images/profile-images/profile-image-13.svg',
    '../../../assets/images/profile-images/profile-image-14.svg',
    '../../../assets/images/profile-images/profile-image-15.svg',
    '../../../assets/images/profile-images/profile-image-16.svg',
    '../../../assets/images/profile-images/profile-image-17.svg',
    '../../../assets/images/profile-images/profile-image-18.svg',
    '../../../assets/images/profile-images/profile-image-19.svg',
    '../../../assets/images/profile-images/profile-image-20.svg',
    '../../../assets/images/profile-images/profile-image-21.svg',
    '../../../assets/images/profile-images/profile-image-22.svg',
  ]

  nextRequiredAgreement$: ReplaySubject<Agreement> =
    new ReplaySubject<Agreement>();

  nextOptionalAgreements$: ReplaySubject<Agreement[]> = new ReplaySubject<
    Agreement[]
  >();

  // state
  private tokenState: ReplaySubject<string | null> = new ReplaySubject<
    string | null
  >(1);

  token$: Observable<string | null> = this.tokenState.asObservable();

  private refreshTokenState: ReplaySubject<string | null> = new ReplaySubject<
    string | null
  >(1);

  refreshToken$: Observable<string | null> = this.refreshTokenState.asObservable();

  user$: Observable<any> = this.token$.pipe(
    switchMap((token) => {
      if (!token && environment.__auth_email === null) {
        return EMPTY;
      }
      const apiUrl = `${environment.baseUrl}/checklogin`;

      return this.httpClient
        .get<APIResponse>(apiUrl)
        .pipe(
          catchError((error) =>
             throwError(() => new Error('Unable to fetch user data'))
          )
        );
    }),
    shareReplay(1)
  );

  userDashboard$: Observable<AuthPermission> = this.token$.pipe(
    switchMap((token) => {
      if (!token) {
        return EMPTY;
      }
      const apiUrl = `${environment.baseUrl}/user/listprivileges`
      return this.httpClient
        .get<any>(apiUrl)
        .pipe(
          catchError((error) =>
            throwError(() => new Error('Unable to fetch user permissions'))
          )
        )
    }),
    shareReplay(1)
  )

  getMyExams(orgID: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/exam/student/listmine?organization_id=${encodeParam(orgID)}`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  getMyClassroomAssessments(page: number, take: number, search: string, active?: boolean, open?: boolean, closest?: boolean, upcoming?: boolean, past?: boolean, type?: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/student/classroom/listmine?page=${encodeParam(page)}&take=${encodeParam(take)}&search=${encodeParam(search)}`;
    if(active)
      apiUrl += `&active=${encodeParam(active)}`;
    if(open)
      apiUrl += `&open=${encodeParam(open)}`;
    if(closest)
      apiUrl += `&closest=${encodeParam(closest && !past)}`;
    if(upcoming)
      apiUrl += `&upcoming=${encodeParam(upcoming)}`;
    if(past)
      apiUrl += `&past=${encodeParam(past)}`;
    if(type && type !== 'All Types')
      apiUrl += `&type=${encodeParam(type)}`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  getOptionalExams(orgID: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/list/optin/mine?organization_id=${encodeParam(orgID)}`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  optInExam(course_id: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/optin?course_id=${encodeParam(course_id)}`
    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  // email login
  emailLogin(email: string, password: string): void {
    // TODO: Email login functionality here
    // this.router.navigate(['login/terms']);
  }

  // netID login
  netIDLogin(): void {
    // const currentURL = window.location.href
    // window.location.href = `https://${environment.domain}.${environment.region}.amazoncognito.com/oauth2/authorize?identity_provider=${environment.sso}&redirect_uri=${currentURL}&response_type=TOKEN&client_id=${environment.clientId}&scope=${environment.scope}`
    if(environment.cognitoUrl != null)
      window.location.href = environment.cognitoUrl
  }

  setToken(token: string | null): void {
    this.tokenState.next(token);

    if (token === null) {
      return;
    }

    localStorage.setItem('token', token);
  }

  setRefreshToken(refreshToken: string | null): void {
    this.refreshTokenState.next(refreshToken);

    if (refreshToken === null) {
      return;
    }

    localStorage.setItem('refresh_token', refreshToken);
  }

  // necessary if we are accounting for changes in permissions while a user is logged in
  // if we are looking for a more static permission system, we can include the user permissions in the user model.
  checkPermissions(): Observable<APIResponse> {
    if(environment.__auth_email != null) {
      console.log('hello world');
    }
    const apiUrl = `${environment.baseUrl}/user/listprivileges`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        if (response.success) {
          this.lastKnownPrivileges = response
        }
        return response
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error)
        // Runs from a route guard on every navigation; fall back to the last known privileges
        // instead of kicking the user out (e.g. a student mid-exam on a network blip or a 500).
        if (this.lastKnownPrivileges != null) {
          return of(this.lastKnownPrivileges)
        }
        if (error.status === 500) {
          localStorage.removeItem('token');
          window.location.replace(`${window.location.origin}/ServerError`)
        }
        return of(toApiErrorResponse(error))
      })
    )
  }

  fixLogin(): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/checklogin/fix`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  logout(): void {
    // TODO need to make an API call to the backend to log the user out (aka delete the session cookie from the DB)
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    this.lastKnownPrivileges = null;
    this.tokenState.next(null);
    this.refreshTokenState.next(null);
    //this.router.navigate(['login']);
    // Clear session storage
    if (window.sessionStorage) {
      window.sessionStorage.clear();
    }
    if(environment.cognitoUrl != null)
      window.location.href = `https://${environment.domain}.${environment.region}.amazoncognito.com/logout?client_id=${environment.clientId}&redirect_uri=${window.location.origin.toString()}/login`
  }

  // respond to required agreement
  acceptRequiredAgreement(agreement_id: string): void {
    const apiUrl = `${environment.baseUrl}/agreements/pending/respond?accept_ids=` + encodeParam(agreement_id);

    // Call API and on success, remove the agreement from the list and serve next one
    this.httpClient
      .post<APIResponse>(apiUrl, {})
      .subscribe((response) => {
        if (response.success) {
          this.requiredAgreements = this.requiredAgreements.slice(1);
          if (this.requiredAgreements.length > 0) {
            this.nextRequiredAgreement$.next(this.requiredAgreements[0]);
          } else {
            // Send null object to the terms page to indicate that there are no more agreements to accept
            this.nextRequiredAgreement$.complete();
          }
        }
      });
  }

  getResponsesPage(agreement_id: number, page: number, size: string): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/agreements/manage/listresponses?agreement_id=` + encodeParam(agreement_id) + '&page=' + encodeParam(page) + '&limit=' + encodeParam(size);

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  getProfileDetails(email: string): Observable<APIResponse> {
    let apiUrl;
    if(email === 'mine')
      apiUrl = `${environment.baseUrl}/organization/users/details/mine`
    else
      apiUrl = `${environment.baseUrl}/organization/users/details/other?email=` + encodeParam(email)

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  getProfileAgreements(email: string): Observable<APIResponse> {
    let apiUrl;
    if(email === 'mine')
      apiUrl = `${environment.baseUrl}/agreements/responses/mine`
    else
      apiUrl = `${environment.baseUrl}/agreements/responses/other?email=` + encodeParam(email)

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    )
  }

  saveProfileEdit(user_email: string, name: string, pronouns: string, department: string, role: string, secondaryEmails: string[], removedAltEmails: string[], profile_pic: number | null): Observable<APIResponse> {
    let apiUrl: string;
    if(user_email === 'mine')
      apiUrl = `${environment.baseUrl}/organization/users/details/update/mine?display_name=` + encodeParam(name)
    else
      apiUrl = `${environment.baseUrl}/organization/users/details/update/other?user_email=` + encodeParam(user_email)  + '&display_name=' + encodeParam(name)

    apiUrl = apiUrl + '&pronouns=' + encodeParam(pronouns) + '&description=' + encodeParam(department) + '&profile_pic=' + encodeParam(profile_pic);

    if(secondaryEmails[0] !== removedAltEmails[0]) {
      secondaryEmails.forEach((email) => {
        apiUrl = apiUrl + '&add_email_alt=' + encodeParam(email)
      })

      removedAltEmails.forEach((email) => {
        apiUrl = apiUrl + '&remove_email_alt=' + encodeParam(email)
      })
    }

    // TODO need to do the secondary email

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getTokens(code: string): Observable<APIResponse> {
    const url = `https://${environment.domain}.us-east-1.amazoncognito.com/oauth2/token`
    let body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', environment.clientId)
      .set('code', code)
      .set('redirect_uri', window.location.origin.toString() + '/login')

    return this.httpClient.post<APIResponse>(url, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getNewAccessToken(): Observable<string> {
    const refreshToken = <string>localStorage.getItem('refresh_token');
    const url = `https://${environment.domain}.us-east-1.amazoncognito.com/oauth2/token`
    let body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', environment.clientId)
      .set('refresh_token', refreshToken)

    return this.httpClient.post<any>(url, body).pipe(
      tap((response : any) => {
        this.setToken(response.access_token)
        if(response.refresh_token) {
          this.setRefreshToken(response.refresh_token)
        }
      }),
      map(response => response.access_token),
      catchError((error) => {
        console.error(error)
        if(error.error?.error === 'invalid_grant') {
          this.logout();
        }
        return throwError(() => error)
      })
    )
  }
}
